import { AppSignUpForm } from "@faire/deprecated-retailer-visitor-shared/components/AppSignUp/AppSignUpForm";
import { Disclaimer } from "@faire/deprecated-retailer-visitor-shared/components/AppSignUp/components/Disclaimer";
import { useAppSignUpState } from "@faire/deprecated-retailer-visitor-shared/components/AppSignUp/useAppSignUpState";
import { getDefaultImageUrl } from "@faire/deprecated-retailer-visitor-shared/components/Image/ProgressiveProductImage";
import { signUpCache } from "@faire/deprecated-retailer-visitor-shared/features/SignUp/SignUpRequestData";
import { SignUpSource } from "@faire/deprecated-retailer-visitor-shared/lib/popSignUp";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { getWindow } from "@faire/web/common/globals/getWindow";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { NonBreaking } from "@faire/web/common/string";
import { IconButton } from "@faire/web/slate-deprecated/IconButton";
import { Chevron } from "@faire/web/slate/icons/foundation/Chevron";
import { Column, Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, { useCallback, useEffect } from "react";

import { isFaireDirectVariant } from "./AppSignUpModalVariant";
import { useLightGatingStore } from "./LightGatingContextStore";
import { ShortSignInLink } from "./SignInInformation";

export const AppSignUpDrawer: React.FC = () => {
  const {
    email,
    modalVariant,
    handleSubmit,
    setEmailValidationErrors,
    markUserEntersEmail,
    handleModalClose,
  } = useAppSignUpState();

  const { isDrawerMinimized, onDrawerMinimized, setIsDrawerMinimized, image } =
    useLightGatingStore();

  const onClose = useCallback(() => {
    handleModalClose();
    onDrawerMinimized();
  }, [handleModalClose, onDrawerMinimized]);

  useEffect(() => {
    // we want a fresh sign up request when user lands on the homepage
    signUpCache.clear();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    const window = getWindow();
    if (window) {
      window.document.addEventListener("keydown", handleKeyDown);
      return () => {
        window.document.removeEventListener("keydown", handleKeyDown);
      };
    }
    return () => {};
  }, [onClose]);

  if (isDrawerMinimized) {
    return (
      <Row
        onClick={() => setIsDrawerMinimized(false)}
        align="center"
        justify="space-between"
        className="relative px-4 py-5 lg:py-7 lg:hover:cursor-pointer"
        data-test-id="app-sign-up-drawer-fold"
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <Typography>
            <StrictLocalMsg
              id="lHGtyU"
              defaultMessage="Unlock wholesale pricing"
              description={{
                text: "Title of the folded sign up modal",
              }}
            />
          </Typography>
        </div>
        <div className="relative flex w-full justify-end">
          <Chevron direction="up" />
        </div>
      </Row>
    );
  }

  return (
    <Column
      align={{
        mobileAndAbove: "stretch",
        tabletAndAbove: "center",
      }}
      justify="center"
      className="pt-2 md:py-0"
      data-test-id="app-sign-up-drawer-unfolded"
    >
      <div className="flex w-full justify-end pb-2 md:pb-0">
        <IconButton
          aria-label="Fold"
          variant="tertiary"
          onClick={onClose}
          data-test-id="fold-drawer-button"
          size="small"
        >
          <Chevron direction="down" />
        </IconButton>
      </div>
      <Column justify="center" align="center" flex={1} className="-mt-4 px-4">
        {image ? <DrawerFeaturedImage image={image} /> : null}
        <Typography variant="displaySSerifRegular" as="h2">
          <StrictLocalMsg
            id="SmWqBO"
            defaultMessage="Unlock wholesale pricing"
            values={{
              nbsp: NonBreaking.space,
            }}
            description={{
              text: "Title of the sign up modal for an experiment",
            }}
          />
        </Typography>
        <Spacer height="2x" />
        <AppSignUpForm
          email={email}
          handleSubmit={handleSubmit}
          setEmailValidationErrors={setEmailValidationErrors}
          markUserEntersEmail={markUserEntersEmail}
          modalVariant={modalVariant}
          emailClickSignUpSource={SignUpSource.LIGHT_GATE_BOTTOM_BANNER}
        />
        <Spacer height="1x" />
        <ShortSignInLink />
        <Spacer height="3x" />
      </Column>
      <div className="bg-core-surface-subdued flex w-full justify-center py-2">
        <Disclaimer
          isFaireDirectSignUp={isFaireDirectVariant(modalVariant)}
          textAlign="center"
        />
      </div>
    </Column>
  );
};

export const DrawerFeaturedImage: React.FC<{
  image: IImage;
}> = ({ image }) => {
  const isSSR = useIsSSR();
  return (
    <img
      src={getDefaultImageUrl(image, isSSR, {})}
      alt="Sign up featured image"
      className="-mt-6 mb-4 h-[72px] w-[72px] rounded object-cover md:-mt-4 md:h-[64px] md:w-[64px]"
    />
  );
};
